/**
 * RaceDebugPrimitives — shared UI atoms for the Race Debug overlay panel.
 *
 * Extracted from RaceDebugOverlay so section components stay thin and the
 * styling is reusable. Follows the same "small presentational components"
 * pattern used by the DevTools tabs (TabBar, etc.).
 */

import React from 'react';

/** Small inline button used in the panel header (clear / collapse / off). */
export const panelBtnStyle: React.CSSProperties = {
  background: 'none',
  border: '1px solid rgba(255,255,255,0.15)',
  color: '#888',
  cursor: 'pointer',
  fontSize: '10px',
  padding: '1px 6px',
  borderRadius: '3px',
};

/** Color for a race player status string. */
export function statusColor(status: string): string | undefined {
  if (status === 'finished' || status === 'racing') return '#2ecc71';
  if (status === 'spectating') return '#f39c12';
  if (status === 'dnf') return '#e74c3c';
  return undefined;
}

/** A labeled section with a colored header. */
export const Section: React.FC<{
  title: string;
  color: string;
  children: React.ReactNode;
}> = ({ title, color, children }) => (
  <div style={{ marginBottom: '6px' }}>
    <div style={{ color, fontSize: '10px', fontWeight: 'bold', marginBottom: '2px' }}>
      {title}
    </div>
    {children}
  </div>
);

/** A label/value row — the atomic unit of the panel body. */
export const Row: React.FC<{
  label: string;
  value: string;
  valueColor?: string;
}> = ({ label, value, valueColor }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px' }}>
    <span style={{ color: '#666' }}>{label}</span>
    <span style={{ color: valueColor ?? '#ccc', textAlign: 'right' }}>{value}</span>
  </div>
);

/** A single event-log entry line (timestamp + type + summary). */
export const LogEntry: React.FC<{
  ts: number;
  type: string;
  summary: string;
}> = ({ ts, type, summary }) => (
  <div style={{ fontSize: '10px', color: '#888', marginBottom: '1px' }}>
    <span style={{ color: '#666' }}>
      {new Date(ts).toLocaleTimeString('en', { hour12: false })}{' '}
    </span>
    <span style={{ color: '#aaa' }}>{type}</span>
    {summary && (
      <span style={{ color: '#555' }}> {summary.slice(0, 60)}</span>
    )}
  </div>
);
